import type { MfgMaterial } from "@/lib/manufacturingClient";
import { inventoryPoNeedsBelowReorder } from "@/lib/manufacturing/poDraftFromInventory";
import {
  appendInventoryNeeds,
  type PoDraftLine,
} from "@/components/manufacturing/purchaseOrderDraftState";
import { CcEmailChipInput } from "@/components/manufacturing/CcEmailChipInput";
import {
  ghostBtnClass,
  inputClass,
  mfgAccentBtnClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";

export function GeneratePurchaseOrderSaveBar(props: {
  materials: MfgMaterial[];
  plantId: string;
  lines: PoDraftLine[];
  onLines: (lines: PoDraftLine[]) => void;
  confirmation: string;
  setConfirmation: (v: string) => void;
  ccEmails: string[];
  setCcEmails: (v: string[]) => void;
  enabled: boolean;
  busy: boolean;
  readOnlyHeader: boolean;
  hasIssuedActive: boolean;
  msg: { text: string; ok: boolean } | null;
  onMsg: (msg: { text: string; ok: boolean }) => void;
  onSaveDraft: () => void;
  onIssueAndPdf: () => void;
  onFinalize?: () => void;
  canFinalize?: boolean;
  finalized?: boolean;
  canIssue?: boolean;
  isPlantRequest?: boolean;
  acknowledged?: boolean;
  expectedDeliveryOn?: string;
  onExpectedDeliveryOn?: (v: string) => void;
  onAcknowledge?: () => void;
}) {
  function fillFromBelowReorder() {
    if (props.readOnlyHeader) return;
    const next = appendInventoryNeeds(
      props.lines,
      inventoryPoNeedsBelowReorder(props.materials, props.plantId)
    );
    if (next.added === 0) {
      props.onMsg({
        text: "No materials below reorder for this plant (set reorder on the material, then count).",
        ok: false,
      });
      return;
    }
    props.onLines(next.lines);
    props.onMsg({
      text: `Added ${next.added} below-reorder line(s). Review qty, vendor, and predicted $.`,
      ok: true,
    });
  }

  return (
    <>
      {props.msg ? (
        <div
          className={
            props.msg.ok
              ? "rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-100"
              : "rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-900/30 dark:text-rose-100"
          }
        >
          {props.msg.text}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={ghostBtnClass}
          disabled={!props.enabled || props.busy || props.readOnlyHeader}
          onClick={fillFromBelowReorder}
        >
          Fill from below-reorder
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          disabled={!props.enabled || props.busy || props.readOnlyHeader}
          onClick={props.onSaveDraft}
        >
          {props.hasIssuedActive ? "Save changes" : "Save draft"}
        </button>
        {props.isPlantRequest && props.onAcknowledge && !props.acknowledged ? (
          <button
            type="button"
            className={ghostBtnClass}
            disabled={!props.enabled || props.busy}
            onClick={props.onAcknowledge}
          >
            Acknowledge request
          </button>
        ) : null}
        {props.canIssue !== false ? (
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            disabled={!props.enabled || props.busy}
            onClick={props.onIssueAndPdf}
          >
            {props.hasIssuedActive ? "Download PDF" : "Issue & download PDF"}
          </button>
        ) : null}
        {props.canFinalize && props.onFinalize ? (
          <button
            type="button"
            className={mfgAccentBtnClass}
            disabled={!props.enabled || props.busy}
            onClick={props.onFinalize}
          >
            Finalize PO
          </button>
        ) : null}
      </div>
      {props.canFinalize ? (
        <p className="text-[11px] text-gray-500">
          Materials and amounts stay editable until you finalize this PO.
        </p>
      ) : null}
      {props.finalized ? (
        <p className="text-[11px] text-gray-500">Finalized. Materials and amounts are locked.</p>
      ) : null}

      {props.isPlantRequest ? (
        <label className="block w-full text-xs">
          <span className="font-semibold">Expected timeframe</span>
          <input
            type="date"
            className={`${inputClass} mt-1 max-w-xs`}
            value={props.expectedDeliveryOn ?? ""}
            disabled={!props.enabled || props.busy || props.readOnlyHeader || props.canIssue === false}
            onChange={(e) => props.onExpectedDeliveryOn?.(e.target.value)}
          />
          <span className="mt-0.5 block text-[11px] font-normal text-gray-500">
            Shown to the plant when this request is ordered.
          </span>
        </label>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block w-full text-xs">
          <span className="font-semibold">Confirmation message</span>
          <textarea
            className={`${inputClass} mt-1 w-full`}
            rows={2}
            value={props.confirmation}
            disabled={!props.enabled || props.busy || props.readOnlyHeader}
            onChange={(e) => props.setConfirmation(e.target.value)}
            placeholder="Org default used when blank on create"
          />
        </label>
        <label className="block w-full text-xs">
          <span className="font-semibold">CC emails</span>
          <CcEmailChipInput
            emails={props.ccEmails}
            onChange={props.setCcEmails}
            disabled={!props.enabled || props.busy || props.readOnlyHeader}
            placeholder="Type an email, then Enter or comma"
          />
          <span className="mt-0.5 block text-[11px] font-normal text-gray-500">
            Printed as a comma-delimited list on the PO. No limit.
          </span>
        </label>
      </div>
    </>
  );
}
